import { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { apiClient } from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const REASONS = [
  ["REAL_PERSON", "image.report_reason_real_person"],
  ["SEXUAL_CONTENT", "chat.report_reason_sexual"],
  ["HATE", "chat.report_reason_hate"],
  ["HARASSMENT", "chat.report_reason_harassment"],
  ["OTHER", "chat.report_reason_other"],
] as const;

interface ReportImageDialogProps {
  /** Generated photo to report; the dialog is open while it is set. */
  imageId: string | null;
  onClose: () => void;
  onReported: (imageId: string) => void;
}

/** Store policy: the user can report any photo the AI generated for them. */
export const ReportImageDialog = ({ imageId, onClose, onReported }: ReportImageDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [reason, setReason] = useState<string>("REAL_PERSON");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!imageId) return;
    setSubmitting(true);
    try {
      await apiClient.reportGeneratedImage(imageId, reason);
      toast({ title: t("image.report_submitted") });
      onReported(imageId);
      onClose();
    } catch (e) {
      console.error("Image report failed", e);
      toast({ title: t("image.report_failed"), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={imageId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("image.report_title")}</DialogTitle>
          <DialogDescription>{t("image.report_description")}</DialogDescription>
        </DialogHeader>
        <RadioGroup value={reason} onValueChange={setReason} className="grid gap-3 py-2">
          {REASONS.map(([value, key]) => (
            <div key={value} className="flex items-center space-x-2">
              <RadioGroupItem value={value} id={`image-report-${value}`} />
              <Label htmlFor={`image-report-${value}`} className="font-normal cursor-pointer">
                {t(key)}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <div className="flex gap-2 justify-end mt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("chat.report_cancel")}
          </Button>
          <Button type="button" variant="romantic" disabled={submitting} onClick={() => void submit()}>
            {submitting ? "…" : t("chat.report_submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
